import { getExportLink } from '../api/exports.api'
import type { ExportFormat, ExportJob, ExportLinkResponse } from '../api/contracts'

/** How often a waiting or running export is read once it's been going a while. */
export const EXPORT_POLL_MS = 2_000
/** How often while it's young: most exports finish within seconds. */
export const EXPORT_QUICK_POLL_MS = 400
const EXPORT_QUICK_POLL_FOR_MS = 15_000

/** The poll interval for exports still going: quick while any is young. */
export function exportPollMs(
  jobs: Pick<ExportJob, 'created_at'>[],
  now: number = Date.now()
): number {
  return jobs.some((job) => now - Date.parse(job.created_at) < EXPORT_QUICK_POLL_FOR_MS)
    ? EXPORT_QUICK_POLL_MS
    : EXPORT_POLL_MS
}

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: 'CSV',
  xlsx: 'Excel',
  pdf: 'PDF',
}

export function isExportFinished(job: Pick<ExportJob, 'status'>): boolean {
  return job.status === 'done' || job.status === 'failed' || job.status === 'canceled'
}

export function isExportActive(job: Pick<ExportJob, 'status'>): boolean {
  return job.status === 'queued' || job.status === 'running'
}

const countFormat = new Intl.NumberFormat()

/** "Waiting to start", "12,400 of 80,000 rows", "Ready", ... */
export function describeExportProgress(job: ExportJob): string {
  switch (job.status) {
    case 'queued':
      return 'Waiting to start'
    case 'running':
      if (job.rows_total) {
        return `${countFormat.format(Math.min(job.rows_written, job.rows_total))} of ${countFormat.format(job.rows_total)} rows`
      }
      return job.rows_written ? `${countFormat.format(job.rows_written)} rows` : 'Starting'
    case 'done':
      return 'Ready'
    case 'failed':
      return 'Failed'
    case 'canceled':
      return 'Canceled'
  }
}

export const EXPORT_MIME_TYPES: Record<ExportFormat, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
}

const EXPORT_EXTENSIONS: Record<ExportFormat, string> = {
  csv: '.csv',
  xlsx: '.xlsx',
  pdf: '.pdf',
}

/** How long a fetched download link is used: links last 5 minutes. */
export const EXPORT_LINK_FRESH_MS = 4 * 60_000

type ExportFile = Pick<ExportJob, 'export_id' | 'file_name' | 'format'>

interface WritableFile {
  createWritable(): Promise<WritableStream<Uint8Array>>
}

type SaveFilePicker = (options: {
  suggestedName: string
  types: { description: string; accept: Record<string, string[]> }[]
}) => Promise<WritableFile>

/** Chrome and Edge's Save As dialog, where the browser has one. */
function saveFilePicker(): SaveFilePicker | null {
  const picker = (window as { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker
  return typeof picker === 'function' ? picker.bind(window) : null
}

/**
 * Whether a click asks where to save a file (Chrome, Edge), so its button
 * says "Save as…"; elsewhere it downloads, and says "Download".
 */
export function canSaveAs(): boolean {
  return saveFilePicker() !== null
}

/**
 * Download a finished export. With `saveAs` (a click, where the browser has
 * Save As: Chrome and Edge), ask where to save it first, inside the click,
 * then stream the file there; closing the dialog cancels quietly. Otherwise,
 * or without a click to ask in, the browser downloads it as it does any file.
 */
export async function downloadExport(
  job: ExportFile,
  { saveAs = false }: { saveAs?: boolean } = {}
): Promise<void> {
  const picker = saveAs ? saveFilePicker() : null
  if (picker) {
    let target: WritableFile | null = null
    try {
      target = await picker({
        suggestedName: job.file_name,
        types: [
          {
            description: `${EXPORT_FORMAT_LABELS[job.format]} file`,
            accept: { [EXPORT_MIME_TYPES[job.format]]: [EXPORT_EXTENSIONS[job.format]] },
          },
        ],
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      // No click to ask in (or the browser refused): download as usual.
    }
    if (target) {
      await saveTo(target, job.export_id)
      return
    }
  }
  const link = await getExportLink(job.export_id)
  const anchor = document.createElement('a')
  anchor.href = link.url
  anchor.download = link.file_name
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

interface WritableFolder {
  getFileHandle(name: string, options?: { create?: boolean }): Promise<WritableFile>
}

type FolderPicker = (options?: { mode?: 'read' | 'readwrite' }) => Promise<WritableFolder>

/** Chrome and Edge's folder dialog, where the browser has one. */
function folderPicker(): FolderPicker | null {
  const picker = (window as { showDirectoryPicker?: FolderPicker }).showDirectoryPicker
  return typeof picker === 'function' ? picker.bind(window) : null
}

/**
 * Whether a click on a selection asks for a folder to save it in (Chrome,
 * Edge), so its button says "Save to folder…"; elsewhere each file
 * downloads, and it says "Download".
 */
export function canSaveToFolder(): boolean {
  return folderPicker() !== null
}

/**
 * Download several finished exports as separate files. Where the browser has
 * a folder dialog (Chrome, Edge), ask once for a folder, inside the click, and
 * write each file there, numbering a name the folder already has instead of
 * replacing it; closing the dialog cancels quietly. Otherwise each downloads
 * as any file does.
 */
export async function downloadExports(jobs: ExportFile[]): Promise<void> {
  if (jobs.length === 1) return downloadExport(jobs[0], { saveAs: true })
  const picker = folderPicker()
  if (picker) {
    let folder: WritableFolder | null = null
    try {
      folder = await picker({ mode: 'readwrite' })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      // No click to ask in (or the browser refused): download as usual.
    }
    if (folder) {
      const taken = new Set<string>()
      for (const job of jobs) {
        const name = await freeName(folder, job.file_name, taken)
        await saveTo(await folder.getFileHandle(name, { create: true }), job.export_id)
      }
      return
    }
  }
  for (const job of jobs) await downloadExport(job)
}

/** `name`, or "name (2).ext" and so on when the folder or this batch has it. */
async function freeName(folder: WritableFolder, name: string, taken: Set<string>): Promise<string> {
  const dot = name.lastIndexOf('.')
  const stem = dot > 0 ? name.slice(0, dot) : name
  const extension = dot > 0 ? name.slice(dot) : ''
  for (let count = 1; ; count += 1) {
    const candidate = count === 1 ? name : `${stem} (${count})${extension}`
    if (taken.has(candidate.toLowerCase())) continue
    try {
      await folder.getFileHandle(candidate)
    } catch {
      taken.add(candidate.toLowerCase())
      return candidate
    }
  }
}

async function saveTo(target: WritableFile, exportId: string): Promise<void> {
  const link = await getExportLink(exportId)
  const response = await fetch(link.url)
  if (!response.ok || !response.body) {
    throw new Error("The file couldn't be read. Download it again in a moment.")
  }
  await response.body.pipeTo(await target.createWritable())
}

/** A finished export's file, read whole (a PDF to open in a tab). */
export async function fetchExportFile(job: ExportFile, signal?: AbortSignal): Promise<File> {
  const link = await getExportLink(job.export_id, signal)
  const response = await fetch(link.url, { signal })
  if (!response.ok) throw new Error("The file couldn't be read. Try again in a moment.")
  return new File([await response.blob()], job.file_name, { type: EXPORT_MIME_TYPES[job.format] })
}

/** Office's "open this file" link: Excel reads it from the URL itself. */
const OPEN_IN_EXCEL = 'ms-excel:ofe|u|'
/** How long an opened PDF's address lasts, once its tab has read it. */
const OPENED_FILE_MS = 60_000

/** Open's label: a PDF opens in the browser, a spreadsheet in Excel. */
export function openLabel(job: Pick<ExportJob, 'file_name' | 'format'>): string {
  // The row names the file; its buttons say only what they do.
  return job.format === 'pdf' ? 'Open in new tab' : 'Open in Excel'
}

/**
 * Open a finished export where it's read: a PDF in a new browser tab (opened
 * inside the click, filled once the file is read); a spreadsheet or CSV in
 * Excel, through Office's `ms-excel:` link, which Excel follows to download
 * the file itself. `link` is a fresh one already in hand, so Excel opens
 * inside the click; without it, one is read first.
 */
export async function openExport(job: ExportFile, link?: ExportLinkResponse): Promise<void> {
  if (job.format === 'pdf') {
    const tab = window.open('', '_blank')
    try {
      const url = URL.createObjectURL(await fetchExportFile(job))
      if (tab) tab.location.href = url
      else window.open(url, '_blank')
      window.setTimeout(() => URL.revokeObjectURL(url), OPENED_FILE_MS)
    } catch (error) {
      tab?.close()
      throw error
    }
    return
  }
  const { url } = link ?? (await getExportLink(job.export_id))
  const anchor = document.createElement('a')
  anchor.href = `${OPEN_IN_EXCEL}${url}`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

/** Whether a row can be dragged out as a file: Chromium's `DownloadURL`. */
export function canDragDownloads(): boolean {
  const brands = (navigator as { userAgentData?: { brands?: { brand: string }[] } }).userAgentData
    ?.brands
  if (brands) return brands.some((entry) => /Chromium/i.test(entry.brand))
  return /\b(Chrome|Chromium|Edg)\//.test(navigator.userAgent)
}

/** Chromium's `DownloadURL` drag data: "<type>:<file name>:<absolute url>". */
export function downloadUrlData(type: string, fileName: string, url: string): string {
  return `${type}:${fileName}:${url}`
}

/** A zip of several exports, for `downloadUrlData`. */
export const EXPORT_BUNDLE_MIME_TYPE = 'application/zip'
